/**
 * Vue Exceptions — anomalies bancaires groupées par catégorie (spec §5.3)
 * Backend: GET /api/bank/exceptions
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fxof = (v) => (v == null ? '—' : `${Number(v).toLocaleString('fr-FR')} XOF`);
const fmtDate = (v) => { if (!v) return '—'; try { return new Date(v).toLocaleDateString('fr-FR'); } catch { return String(v); } };

const CATEGORY_STYLES = {
  old_unmatched: { color: 'rose', label: 'Non rapprochées > 15 jours', icon: '⏰' },
  to_verify: { color: 'amber', label: 'OCR faible confiance', icon: '🔍' },
  in_gap: { color: 'rose', label: 'Écart de rapprochement', icon: '⚠️' },
  unjustified_debits: { color: 'orange', label: 'Débits non justifiés', icon: '💸' },
  uncategorized_fees: { color: 'violet', label: 'Frais bancaires non classés', icon: '🏛️' },
};

export const ExceptionsView = ({ accountId, yyyyMm, refreshKey = 0, onReconcile }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set(['old_unmatched']));
  const [tick, setTick] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (accountId) params.account_id = accountId;
        if (yyyyMm) params.yyyy_mm = yyyyMm;
        const { data } = await axios.get(`${API}/bank/exceptions`, { params });
        setData(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [accountId, yyyyMm, refreshKey, tick]);

  const toggle = (k) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  return (
    <div className="space-y-3" data-testid="bank-exceptions-view">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <h3 className="text-sm font-semibold">Exceptions ({data?.total ?? 0})</h3>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setTick(t => t + 1)} data-testid="bank-exceptions-refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {loading && <div className="text-xs text-zinc-500">Chargement...</div>}

      {data && Object.entries(data.categories || {}).map(([key, cat]) => {
        const style = CATEGORY_STYLES[key] || { color: 'zinc', label: cat.label, icon: '•' };
        const isOpen = expanded.has(key);
        const hasItems = cat.count > 0;
        return (
          <div key={key} className={`rounded-lg border border-${style.color}-200 bg-${style.color}-50/30 overflow-hidden`} data-testid={`bank-exception-${key}`}>
            <button
              type="button"
              onClick={() => hasItems && toggle(key)}
              disabled={!hasItems}
              className={`w-full flex items-center justify-between px-3 py-2 text-left ${hasItems ? 'hover:bg-white/50 cursor-pointer' : 'opacity-60 cursor-default'}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{style.icon}</span>
                <span className="text-sm font-semibold text-zinc-800">{style.label}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${hasItems ? `bg-${style.color}-200 text-${style.color}-800` : 'bg-zinc-200 text-zinc-600'}`}>
                  {cat.count}
                </span>
              </div>
              {hasItems && (isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
            </button>
            {isOpen && hasItems && (
              <div className="border-t border-zinc-200 bg-white/60 divide-y divide-zinc-100 max-h-64 overflow-y-auto">
                {cat.items.map(tx => (
                  <div key={tx.id} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-zinc-50">
                    <span className="text-zinc-500 font-mono min-w-[80px]">{fmtDate(tx.operation_date)}</span>
                    <span className="text-zinc-700 truncate flex-1">{tx.label}</span>
                    <span className={`font-semibold min-w-[100px] text-right ${tx.direction === 'credit' ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {tx.direction === 'credit' ? '+' : '−'} {fxof(tx.amount)}
                    </span>
                    {onReconcile && (
                      <Button size="sm" variant="outline" onClick={() => onReconcile(tx)} className="h-6 text-[10px] px-2">
                        Traiter
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ExceptionsView;
